// Service for importing CSV and Excel files to create locations and doctors
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

class FileImportService {
  constructor() {
    this.supportedFormats = ['.csv', '.xlsx', '.xls'];
    this.maxFileSize = 10 * 1024 * 1024; // 10MB
  }

  // Validate file before processing
  validateFile(file) {
    const errors = [];

    // Check file size
    if (file.size > this.maxFileSize) {
      errors.push(`File size must be less than ${this.maxFileSize / (1024 * 1024)}MB`);
    }

    // Check file extension
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!this.supportedFormats.includes(extension)) {
      errors.push(`Unsupported file format. Supported formats: ${this.supportedFormats.join(', ')}`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Parse CSV file
  async parseCSV(file) {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results.errors.length > 0) {
            reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
          } else {
            resolve(results.data);
          }
        },
        error: (error) => {
          reject(new Error(`CSV parsing error: ${error.message}`));
        }
      });
    });
  }

  // Parse Excel file
  async parseExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get the first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to JSON
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            reject(new Error('Excel file must have at least a header row and one data row'));
            return;
          }
          
          // Convert to objects with headers
          const headers = jsonData[0];
          const rows = jsonData.slice(1);
          
          const result = rows.map(row => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] || '';
            });
            return obj;
          });
          
          resolve(result);
        } catch (error) {
          reject(new Error(`Excel parsing error: ${error.message}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read Excel file'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  }

  // Parse file based on extension
  async parseFile(file) {
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    switch (extension) {
      case '.csv':
        return await this.parseCSV(file);
      case '.xlsx':
      case '.xls':
        return await this.parseExcel(file);
      default:
        throw new Error(`Unsupported file format: ${extension}`);
    }
  }

  // Deduplicate doctors by name and merge their locations (same logic as Directory component)
  deduplicateDoctors(doctors) {
    const doctorMap = new Map();
    
    doctors.forEach(doctor => {
      const key = doctor.name.toLowerCase().trim();
      
      if (doctorMap.has(key)) {
        // Merge locations for duplicate doctors
        const existingDoctor = doctorMap.get(key);
        const existingLocationNames = existingDoctor.locationNames || [];
        const newLocationNames = doctor.locationNames || [];
        
        // Combine and deduplicate location names
        const combinedLocationNames = [...new Set([...existingLocationNames, ...newLocationNames])];
        
        // Merge other fields, preferring non-empty values
        const mergedDoctor = {
          ...existingDoctor,
          locationNames: combinedLocationNames,
          // Keep the most complete data for other fields
          phone: doctor.phone || existingDoctor.phone,
          email: doctor.email || existingDoctor.email,
          bio: doctor.bio || existingDoctor.bio,
          specialty: doctor.specialty || existingDoctor.specialty
        };
        
        doctorMap.set(key, mergedDoctor);
      } else {
        doctorMap.set(key, {
          ...doctor,
          locationNames: doctor.locationNames || []
        });
      }
    });
    
    return Array.from(doctorMap.values());
  }

  // Validate and map imported data
  validateAndMapData(rawData) {
    const errors = [];
    const locations = [];
    const doctors = [];
    const locationMap = new Map(); // To track location IDs

    // Expected columns for locations
    const locationColumns = [
      'location_name', 'location_address', 'location_city', 
      'location_state', 'location_zip', 'location_phone', 'location_email'
    ];

    // Expected columns for doctors
    const doctorColumns = [
      'doctor_name', 'doctor_specialty', 'doctor_phone', 
      'doctor_email', 'doctor_bio', 'location_name'
    ];

    // Check if we have the required columns
    if (rawData.length === 0) {
      errors.push('File is empty or has no data rows');
      return { locations, doctors, errors };
    }

    const headers = Object.keys(rawData[0]);
    const missingLocationColumns = locationColumns.filter(col => !headers.includes(col));
    const missingDoctorColumns = doctorColumns.filter(col => !headers.includes(col));

    if (missingLocationColumns.length > 0) {
      errors.push(`Missing required location columns: ${missingLocationColumns.join(', ')}`);
    }

    if (missingDoctorColumns.length > 0) {
      errors.push(`Missing required doctor columns: ${missingDoctorColumns.join(', ')}`);
    }

    if (errors.length > 0) {
      return { locations, doctors, errors };
    }

    // Process each row
    rawData.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because we start from row 2 (after header)

      // Create location if it doesn't exist
      const locationName = row.location_name?.trim();
      if (locationName && !locationMap.has(locationName)) {
        const location = {
          name: locationName,
          address: row.location_address?.trim() || '',
          city: row.location_city?.trim() || '',
          state: row.location_state?.trim() || '',
          zipCode: row.location_zip?.trim() || '',
          phone: row.location_phone?.trim() || '',
          email: row.location_email?.trim() || ''
        };

        // Validate location data
        if (!location.name) {
          errors.push(`Row ${rowNumber}: Location name is required`);
        }

        locations.push(location);
        locationMap.set(locationName, location);
      }

      // Create doctor
      const doctorName = row.doctor_name?.trim();
      if (doctorName) {
        const doctor = {
          name: doctorName,
          specialty: row.doctor_specialty?.trim() || '',
          phone: row.doctor_phone?.trim() || '',
          email: row.doctor_email?.trim() || '',
          bio: row.doctor_bio?.trim() || '',
          locationNames: [] // Will store location names for deduplication
        };

        // Validate doctor data
        if (!doctor.name) {
          errors.push(`Row ${rowNumber}: Doctor name is required`);
        }

        if (!doctor.specialty) {
          errors.push(`Row ${rowNumber}: Doctor specialty is required`);
        }

        // Link doctor to location
        if (locationName && locationMap.has(locationName)) {
          doctor.locationNames = [locationName]; // Store location name for deduplication
        } else if (locationName) {
          errors.push(`Row ${rowNumber}: Location "${locationName}" not found in location data`);
        }

        doctors.push(doctor);
      }
    });

    // Deduplicate doctors before returning
    const deduplicatedDoctors = this.deduplicateDoctors(doctors);
    console.log('Import preview - Original doctors count:', doctors.length);
    console.log('Import preview - Deduplicated doctors count:', deduplicatedDoctors.length);

    return { locations, doctors: deduplicatedDoctors, errors };
  }

  // Generate template data
  generateTemplate() {
    const templateData = [
      {
        location_name: 'Main Office',
        location_address: '123 Main Street',
        location_city: 'Anytown',
        location_state: 'CA',
        location_zip: '12345',
        location_phone: '(555) 123-4567',
        location_email: 'main@example.com',
        doctor_name: 'Dr. John Smith',
        doctor_specialty: 'Cardiology',
        doctor_phone: '(555) 123-4568',
        doctor_email: 'john.smith@example.com',
        doctor_bio: 'Board-certified cardiologist with 10 years of experience.'
      },
      {
        location_name: 'Main Office',
        location_address: '123 Main Street',
        location_city: 'Anytown',
        location_state: 'CA',
        location_zip: '12345',
        location_phone: '(555) 123-4567',
        location_email: 'main@example.com',
        doctor_name: 'Dr. Jane Doe',
        doctor_specialty: 'Oncology',
        doctor_phone: '(555) 123-4569',
        doctor_email: 'jane.doe@example.com',
        doctor_bio: 'Specialized in cancer treatment and research.'
      },
      {
        location_name: 'Downtown Clinic',
        location_address: '456 Downtown Ave',
        location_city: 'Anytown',
        location_state: 'CA',
        location_zip: '12346',
        location_phone: '(555) 987-6543',
        location_email: 'downtown@example.com',
        doctor_name: 'Dr. Bob Johnson',
        doctor_specialty: 'Pediatrics',
        doctor_phone: '(555) 987-6544',
        doctor_email: 'bob.johnson@example.com',
        doctor_bio: 'Pediatric specialist focused on child health and development.'
      }
    ];

    return templateData;
  }

  // Download template as CSV
  downloadTemplate() {
    const templateData = this.generateTemplate();
    const csv = Papa.unparse(templateData);
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'directory_import_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // Get column descriptions for help
  getColumnDescriptions() {
    return {
      location_name: 'Name of the location (e.g., "Main Office", "Downtown Clinic")',
      location_address: 'Street address of the location',
      location_city: 'City where the location is located',
      location_state: 'State abbreviation (e.g., "CA", "NY")',
      location_zip: 'ZIP/postal code',
      location_phone: 'Phone number for the location',
      location_email: 'Email address for the location',
      doctor_name: 'Full name of the doctor (e.g., "Dr. John Smith")',
      doctor_specialty: 'Medical specialty (e.g., "Cardiology", "Oncology")',
      doctor_phone: 'Doctor\'s direct phone number',
      doctor_email: 'Doctor\'s email address',
      doctor_bio: 'Brief biography or description of the doctor'
    };
  }
}

const fileImportService = new FileImportService();
export default fileImportService;
